import React, {createContext, useContext, useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import Spinner from '../../../../common/components/ui/Spinner';
import {Form101FileStatus, FormsState, FormsStateItem, TaxAdjustmentFileStatus} from '../../../../common/types';
import {RootState} from '../../../../store/configureStore';

export type RegFormsContext = {
  status: {
    personalInfo: FormsStateItem,
    contract: FormsStateItem,
    form6101: FormsStateItem,
    form101: FormsStateItem,
    taxAdjustment: FormsStateItem,
  }
  form101FileStatus: {
    exists: boolean
    year: string
  }
  taxAdjustmentFileStatus: {
    exists: boolean
  }
  showMessageFillPersonalInfo: () => void
}

export type RegFormsContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext = {
  status: {
    personalInfo: {
      filled: false,
      link: '/'
    },
    contract: {
      filled: false,
      link: '/'
    },
    form6101: {
      filled: false,
      link: '/'
    },
    form101: {
      filled: false,
      link: '/'
    },
    taxAdjustment: {
      filled: false,
      link: '/'
    },
  },
  form101FileStatus: {
    exists: true,
    year: '2023'
  },
  taxAdjustmentFileStatus: {
    exists: true
  },
  showMessageFillPersonalInfo: () => {}
};

export const RegFormsContext = createContext<RegFormsContext>(defaultContext);
RegFormsContext.displayName = 'RegFormsContext';

export const useRegFormsContext = () => useContext(RegFormsContext);

export const RegFormsContextProvider = ({children}: RegFormsContextProviderProps) => {
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const M = useMessage();

  const initialFormsState = defaultContext.status;
  const initialForm101FileStatus = defaultContext.form101FileStatus;
  const initialTaxAdjustmentFileStatus = defaultContext.taxAdjustmentFileStatus;
  const [formsState, setFormsState] = useState<FormsState>(initialFormsState);
  const [form101FileStatus, setForm101FileStatus] = useState<Form101FileStatus>(initialForm101FileStatus);
  const [taxAdjustmentFileStatus, setTaxAdjustmentFileStatus] = useState<TaxAdjustmentFileStatus>(initialTaxAdjustmentFileStatus);
  const currentLocale = useSelector((state: RootState) => state.i18n.locale, shallowEqual);
  const [loading, setLoading] = useState(true);

  const updateFormsState = async () => {
    try {
      setFormsState(initialFormsState);
      const result = await apiService.getRegFormsState(currentLocale);

      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      }
      setFormsState(result.body.status);
      setForm101FileStatus(result.body.meta.form101FileStatus);
      setTaxAdjustmentFileStatus(result.body.meta.taxAdjustmentFileStatus);
    } catch (e) {
      globalMessage.open(M.serverError);
      setFormsState(initialFormsState);
    }
  };
  const showMessageFillPersonalInfo = () => {
    globalMessage.open(M.fillPersonalDetails);
  };

  useEffect(() => {
    setLoading(true);
    updateFormsState()
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const context: RegFormsContext = {
    status: formsState,
    form101FileStatus,
    taxAdjustmentFileStatus,
    showMessageFillPersonalInfo
  };

  return loading
    ? <Spinner/>
    : (
      <RegFormsContext.Provider value={context}>
        {children}
      </RegFormsContext.Provider>
    );
};
